import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Search, Star, Shield, Sparkles, Award } from 'lucide-react';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import VehicleCard from '../components/VehicleCard';
import { useAnalytics } from '../hooks/useAnalytics';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BRAND_MARQUEE = [
  'Ferrari', 'Lamborghini', 'Porsche', 'Mercedes-Benz', 'BMW', 'Audi',
  'Bentley', 'Rolls-Royce', 'Aston Martin', 'Maserati', 'McLaren', 'Tesla'
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};

/** Mouse-tracking tilt container (subtle 3D feel) */
const TiltCard = ({ children, className = '', max = 8 }) => {
  const ref = useRef(null);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const ry = (x - 0.5) * max * 2;
    const rx = (0.5 - y) * max * 2;
    el.style.setProperty('--rx', `${rx}deg`);
    el.style.setProperty('--ry', `${ry}deg`);
  };

  const handleLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`tilt-3d ${className}`}
    >
      {children}
    </div>
  );
};

const SectionNumber = ({ n, label }) => (
  <motion.div
    variants={fadeUp}
    className="flex items-center gap-4 mb-4"
    data-testid={`section-number-${n}`}
  >
    <span className="section-num">{n}</span>
    <span className="cine-divider flex-1 max-w-[6rem]" />
    <span className="section-num">{label}</span>
  </motion.div>
);

const HomePage = ({ lang }) => {
  const { t } = useTranslation(lang);
  useAnalytics('home');
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const [heroVehicle, setHeroVehicle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [marqueeShrunk, setMarqueeShrunk] = useState(false);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.35]);

  useEffect(() => {
    fetchFeaturedVehicles();
    fetchBrands();
  }, []);

  useEffect(() => {
    const onScroll = () => setMarqueeShrunk(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API}/vehicles/filters/options`);
      const list = (res.data?.brands || []).filter(Boolean);
      if (list.length > 0) setBrands(list);
    } catch (e) {
      console.error('fetchBrands failed:', e?.response?.status, e?.message);
    }
  };

  const fetchFeaturedVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles?featured=true&limit=100`);
      let data = response.data || [];
      // Fallback: if no featured, show first premium available vehicles
      if (data.length === 0) {
        const fallback = await axios.get(`${API}/vehicles?limit=6`);
        data = fallback.data || [];
      }
      setFeaturedVehicles(data);
      // Pick the newest / first featured with an image as hero
      const heroCandidate = data.find(v => v.images && v.images.length > 0) || data[0];
      setHeroVehicle(heroCandidate);
    } catch (error) {
      console.error('Error fetching featured vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      window.location.href = `/inventory?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <>
      <SEOHead
        title="AAA of Miami | Premium Luxury & Performance Vehicles"
        description="Discover 350+ premium luxury and performance vehicles at AAA of Miami. Quality pre-owned cars with detailed inspections. BMW, Mercedes-Benz, Porsche & more."
        type="organization"
      />

      <div className="cinematic-bg cine-scope" data-testid="home-cinematic">
        {/* ================== BRAND MARQUEE (TOP - sticky under navbar, shrinks on scroll) ================== */}
        <section
          className={`sticky top-16 z-30 border-b cine-border backdrop-blur transition-all duration-500 ${
            marqueeShrunk ? 'pt-2 pb-1' : 'pt-6 pb-4'
          }`}
          style={{ background: 'color-mix(in oklab, var(--bg-primary) 60%, transparent)' }}
          data-testid="brand-marquee-section"
          data-shrunk={marqueeShrunk ? 'true' : 'false'}
        >
          {!marqueeShrunk && (
            <div className="text-center mb-3">
              <span className="section-num">01 · Trusted Brands</span>
            </div>
          )}
          <div className="marquee" data-testid="brand-marquee">
            <div className="marquee-track">
              {(() => {
                const list = brands.length > 0 ? brands : BRAND_MARQUEE;
                return [...list, ...list].map((b, i) => (
                  <Link
                    key={i}
                    to={`/inventory?brand=${encodeURIComponent(b)}`}
                    className={`font-bold cine-text-faint hover:text-[#d4af37] transition-all duration-500 whitespace-nowrap cursor-pointer ${
                      marqueeShrunk ? 'text-sm md:text-base' : 'text-xl md:text-2xl'
                    }`}
                    style={{ fontFamily: 'Playfair Display' }}
                    data-testid={`brand-link-${b}`}
                  >
                    {b}
                  </Link>
                ));
              })()}
            </div>
          </div>
        </section>

        {/* ================== HERO ================== */}
        <section
          ref={heroRef}
          className="relative min-h-screen flex items-center justify-center overflow-hidden cinematic-grain"
          data-testid="hero-section-v2"
        >
          {/* Video background */}
          <div className="absolute inset-0 z-0 overflow-hidden">
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=1920&q=80"
              className="w-full h-full object-cover"
              data-testid="hero-bg-video"
            >
              <source src="https://videos.pexels.com/video-files/854108/854108-hd_1920_1080_25fps.mp4" type="video/mp4" />
            </video>
            {/* Dark cinematic overlay */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(180deg, var(--cine-overlay-top) 0%, var(--cine-overlay-mid) 40%, var(--cine-overlay-bot) 100%)',
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'radial-gradient(ellipse at 20% 30%, rgba(212,175,55,0.18), transparent 55%), radial-gradient(ellipse at 80% 70%, rgba(220,38,38,0.10), transparent 55%)',
              }}
            />
          </div>

          {/* Ambient glows */}
          <div className="hero-glow-a" style={{ top: '-8rem', left: '-6rem', opacity: 0.35 }} />
          <div className="hero-glow-b" style={{ bottom: '-4rem', right: '-4rem', opacity: 0.3 }} />

          {/* Content */}
          <div className="relative z-10 max-w-5xl w-full mx-auto px-6 text-center pt-24">
            {/* Text side */}
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
            >
              <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mb-6">
                <span className="cine-divider flex-1 max-w-[5rem]" />
                <span className="section-num">02 · Showroom</span>
                <span className="cine-divider flex-1 max-w-[5rem]" />
              </motion.div>

              <motion.h1
                variants={fadeUp}
                className="section-heading font-bold mb-6"
                style={{ fontFamily: 'Playfair Display' }}
                data-testid="hero-title"
              >
                <span className="block">{t('hero.title')?.split(' ').slice(0, -1).join(' ') || 'Where Luxury'}</span>
                <span className="gold-gradient-text">{t('hero.title')?.split(' ').slice(-1)[0] || 'Awaits'}</span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="text-lg cine-text-muted mb-10 max-w-2xl mx-auto leading-relaxed"
                data-testid="hero-subtitle"
              >
                {t('hero.subtitle')}
              </motion.p>

              {/* Search */}
              <motion.div variants={fadeUp} className="max-w-lg mx-auto mb-8">
                <div className="flex gap-3 p-1.5 rounded-full border cine-border cine-surface backdrop-blur">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 cine-text-faint" />
                    <Input
                      type="text"
                      placeholder={t('hero.search')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="pl-10 h-11 rounded-full bg-transparent border-0 cine-text-strong placeholder:cine-text-faint focus-visible:ring-0 focus-visible:ring-offset-0"
                      data-testid="hero-search-input"
                    />
                  </div>
                  <Button
                    onClick={handleSearch}
                    className="h-11 px-6 rounded-full bg-[#d4af37] hover:bg-[#e6c355] text-black font-semibold"
                    data-testid="hero-search-btn"
                  >
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex flex-wrap gap-4 justify-center">
                <Link to="/inventory">
                  <Button
                    size="lg"
                    className="h-14 px-8 rounded-full bg-gradient-to-r from-[#d4af37] to-[#f6d980] text-black hover:brightness-110 shadow-[0_10px_30px_-10px_rgba(212,175,55,0.6)]"
                    data-testid="browse-inventory-btn"
                  >
                    {t('hero.browse')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-8 rounded-full cine-border cine-text-strong bg-transparent hover:cine-surface"
                    data-testid="hero-contact-btn"
                  >
                    {t('hero.contactUsCTA') || 'Contact Us'}
                  </Button>
                </Link>
              </motion.div>

              {/* KPI strip */}
              <motion.div
                variants={fadeUp}
                className="mt-12 grid grid-cols-3 gap-6 max-w-lg mx-auto"
              >
                {[
                  { n: '350+', l: t('hero.premiumSelection') || 'Premium Cars' },
                  { n: '4.9', l: 'Google Rating' },
                  { n: '20+', l: 'Years Trust' },
                ].map((k, i) => (
                  <div key={i} className="text-center">
                    <div className="gold-gradient-text text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>{k.n}</div>
                    <div className="text-xs cine-text-faint uppercase tracking-widest mt-1">{k.l}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>

          {/* Scroll cue */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cine-text-faint z-10">
            <span className="text-[10px] tracking-[0.4em] uppercase">Scroll</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              className="w-px h-8 bg-current opacity-30"
            />
          </div>
        </section>

        {/* ================== HERO 02 · SHOWROOM label update ================== */}
        {/* keep placeholder for readability - actual section starts above */}

        {/* ================== BRAND MARQUEE (removed - moved to top) ================== */}

        {/* ================== FEATURED VEHICLES ================== */}
        <section className="relative py-24 px-6" data-testid="featured-section">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="max-w-7xl mx-auto"
          >
            <SectionNumber n="03" label="Featured Fleet" />
            <motion.h2
              variants={fadeUp}
              className="section-heading font-bold mb-4 cine-text-strong"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="featured-title"
            >
              {t('hero.featuredVehicles')?.split(' ')[0] || 'Featured'}{' '}
              <span className="gold-gradient-text">{t('hero.featuredVehicles')?.split(' ').slice(1).join(' ') || 'Vehicles'}</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="cine-text-muted max-w-2xl mb-12">
              {t('hero.handpickedPremium')}
            </motion.p>

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="w-10 h-10 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <motion.div
                variants={stagger}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                data-testid="featured-vehicles-grid"
              >
                {featuredVehicles.slice(0, 6).map((vehicle) => (
                  <motion.div key={vehicle.id} variants={fadeUp}>
                    <VehicleCard vehicle={vehicle} lang={lang} />
                  </motion.div>
                ))}
              </motion.div>
            )}

            {!loading && featuredVehicles.length === 0 && (
              <div className="text-center py-20">
                <p className="cine-text-faint">{t('hero.noFeaturedAvailable')}</p>
              </div>
            )}

            <motion.div variants={fadeUp} className="text-center mt-14">
              <Link to="/inventory">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 rounded-full border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-black bg-transparent"
                  data-testid="view-all-vehicles-btn"
                >
                  {t('hero.viewAllVehicles')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </section>

        {/* ================== WHY US ================== */}
        <section className="relative py-24 px-6" data-testid="why-us-section">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="max-w-7xl mx-auto"
          >
            <SectionNumber n="04" label="The Standard" />
            <motion.h2
              variants={fadeUp}
              className="section-heading font-bold mb-4 cine-text-strong"
              style={{ fontFamily: 'Playfair Display' }}
            >
              {t('hero.whyChoose')?.split(' ').slice(0, 2).join(' ') || 'Why Choose'}{' '}
              <span className="gold-gradient-text">{t('hero.whyChoose')?.split(' ').slice(2).join(' ') || 'AAA of Miami'}</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="cine-text-muted mb-12 max-w-2xl">
              {t('hero.trustedPartner')}
            </motion.p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { Icon: Sparkles, title: t('hero.premiumSelection'), desc: t('hero.premiumSelectionDesc'), testid: 'feature-card-inventory' },
                { Icon: Award, title: t('hero.expertService'), desc: t('hero.expertServiceDesc'), testid: 'feature-card-service' },
                { Icon: Shield, title: t('hero.trustedQuality'), desc: t('hero.trustedQualityDesc'), testid: 'feature-card-trust' },
              ].map(({ Icon, title, desc, testid }, i) => (
                <motion.div key={i} variants={fadeUp} data-testid={testid}>
                  <TiltCard className="showcase-card p-8 h-full" max={5}>
                    <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center mb-6">
                      <Icon className="w-5 h-5 text-[#d4af37]" />
                    </div>
                    <h3 className="text-xl font-semibold cine-text-strong mb-3" style={{ fontFamily: 'Playfair Display' }}>
                      {title}
                    </h3>
                    <p className="cine-text-muted text-sm leading-relaxed">{desc}</p>
                  </TiltCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ================== GOOGLE REVIEWS ================== */}
        <section className="relative py-24 px-6 overflow-hidden" data-testid="google-reviews-section">
          <div className="hero-glow-a" style={{ top: '20%', left: '10%', opacity: 0.35 }} />

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-4xl mx-auto text-center relative z-10"
          >
            <SectionNumber n="05" label="Reviews" />

            <motion.div variants={fadeUp} className="flex justify-center mb-6">
              <div className="flex">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className="w-6 h-6 text-[#d4af37] fill-[#d4af37]" />
                ))}
              </div>
              <span className="ml-3 text-2xl font-bold cine-text-strong" style={{ fontFamily: 'Playfair Display' }}>4.9</span>
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="section-heading font-bold mb-6 cine-text-strong"
              style={{ fontFamily: 'Playfair Display' }}
            >
              <span className="gold-gradient-text">{t('google.rateUs') || 'Rate Us on Google'}</span>
            </motion.h2>

            <motion.p variants={fadeUp} className="cine-text-muted mb-10 max-w-2xl mx-auto">
              {t('google.rateUsDesc') || 'Share your experience and help others make the right choice'}
            </motion.p>

            <motion.a
              variants={fadeUp}
              href="https://maps.app.goo.gl/jv25ydmjsk4TMrZZA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 h-14 px-8 rounded-full bg-white text-black font-semibold hover:brightness-95 transition"
              data-testid="google-review-btn"
            >
              <Star className="w-5 h-5 text-[#d4af37] fill-[#d4af37]" />
              {t('google.writeReview') || 'Write a Google Review'}
              <ArrowRight className="w-4 h-4" />
            </motion.a>
          </motion.div>
        </section>

        {/* ================== CTA (06 - removed by user request) ================== */}
      </div>
    </>
  );
};

export default HomePage;
